import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getRoomConfigAsync, hasSkippedRoomSetup } from '@/config/roomConfig';
import { authService } from '@/domains/internal/auth/services/authService';
import RoomSetupModal from '@/domains/internal/facilities/components/RoomSetupModal';
import RoomNotAssignedModal from '@/domains/internal/facilities/components/RoomNotAssignedModal';

interface RoomCheckWrapperProps {
  children: React.ReactNode;
}

/**
 * Wrapper component that checks if room is assigned after user logs in
 * - For regular users and admin: Shows warning and logs them out if no room assigned
 * - For super_admin only: Shows setup modal if no room assigned (can assign rooms)
 *
 * Uses async room lookup with fallback to IP-based identification for cross-browser support
 *
 * IMPORTANT: Only checks room ONCE on initial auth, NOT on every route change
 * to prevent excessive API calls (was causing 15-20 auth requests per page load)
 */
const RoomCheckWrapper: React.FC<RoomCheckWrapperProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showAdminSetup, setShowAdminSetup] = useState(false);
  const [showUserWarning, setShowUserWarning] = useState(false);
  const [checkComplete, setCheckComplete] = useState(false);
  const hasCheckedRef = useRef(false); // Track if we've already checked room

  const performRoomCheck = async () => {
    // Only check if user is authenticated
    if (!authService.isAuthenticated()) {
      hasCheckedRef.current = false; // Reset check flag on logout
      setCheckComplete(true);
      return;
    }

    // Don't check on login/auth pages (including change-password for first-time login)
    if (location.pathname === '/login' || location.pathname === '/forgot-password' || location.pathname === '/reset-password' || location.pathname === '/change-password') {
      setCheckComplete(true);
      return;
    }

    // Skip room check for referral portal routes - referral users don't need room assignments
    if (location.pathname.startsWith('/referral-portal')) {
      hasCheckedRef.current = true; // Mark as checked
      setCheckComplete(true);
      return;
    }

    // Skip room check for referral users
    const user = authService.getUser();
    if (user?.user_type === 'referral') {
      hasCheckedRef.current = true; // Mark as checked
      setCheckComplete(true);
      return;
    }

    // If we've already checked during this session, don't check again
    // This prevents repeated API calls on every route change
    if (hasCheckedRef.current) {
      setCheckComplete(true);
      return;
    }

    try {
      // Check if user has explicitly skipped room setup
      const hasSkipped = hasSkippedRoomSetup();

      // Use async room config with caching (will use cached value on subsequent calls)
      const roomConfig = await getRoomConfigAsync();
      const roomAssigned = roomConfig.roomId !== null;
      const isSuperAdmin = authService.isSuperAdmin();

      // Mark that we've performed the check
      hasCheckedRef.current = true;

      if (!roomAssigned && !hasSkipped) {
        if (isSuperAdmin) {
          // SuperAdmin only: Show setup modal (blocking)
          setShowAdminSetup(true);
          setCheckComplete(false);
        } else {
          // Regular user and admin: Show warning modal
          setShowUserWarning(true);
          setCheckComplete(false);
        }
      } else {
        setCheckComplete(true);
      }
    } catch (error) {
      console.error('[RoomCheck] Error checking room:', error);
      hasCheckedRef.current = true; // Mark as checked even on error
      setCheckComplete(true); // Allow access on error
    }
  };

  // Check room when:
  // 1. Component mounts
  // 2. User navigates away from login/auth pages (after successful login)
  // The hasCheckedRef inside performRoomCheck prevents repeated checks
  useEffect(() => {
    // performRoomCheck has all the logic to:
    // - Skip if not authenticated
    // - Skip if on login/auth pages
    // - Skip if already checked (hasCheckedRef.current = true)
    // So it's safe to call on every pathname change
    performRoomCheck();
  }, [location.pathname]); // Run when pathname changes (e.g., after login)

  const handleAdminSetupSuccess = () => {
    setShowAdminSetup(false);
    setCheckComplete(true);
  };

  const handleUserWarningConfirm = async () => {
    // Log out the user
    await authService.logout();
    setShowUserWarning(false);
    navigate('/login');
  };

  // Don't render children until room check is complete
  if (!checkComplete && showAdminSetup) {
    return (
      <RoomSetupModal
        isOpen={showAdminSetup}
        onClose={() => {}} // Prevent closing
        onSuccess={handleAdminSetupSuccess}
        canClose={false} // Must setup room before proceeding
      />
    );
  }

  if (showUserWarning) {
    return (
      <RoomNotAssignedModal
        isOpen={showUserWarning}
        onConfirm={handleUserWarningConfirm}
      />
    );
  }

  // Room is assigned or check is complete, render children
  return <>{children}</>;
};

export default RoomCheckWrapper;
